"use client";

import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

import { SessionTimeoutModal } from "@repo/ui/components";
import { zoneUrl } from "@repo/ui/constants/bookingBase";
import {
  TAB_ACTIVITY_COOKIE_DELIMITER,
  TAB_ACTIVITY_COOKIE_NAME,
  TAB_ACTIVITY_COOKIE_POLL_INTERVAL_MS,
} from "@repo/ui/constants/session";
import { clearTabSessionId, getTabSessionId } from "@repo/ui/lib/tabSessionId";
import { useZonePathname } from "@repo/ui/navigation";
import type { SessionModal } from "@repo/ui/types/cms";

import { getNewTokenAction } from "./actions/getNewToken";
import { refreshTokenAction } from "./actions/refreshToken";

// Constants
const SESSION_TIMEOUT = 10 * 60 * 1000; // 10 minutes total session timeout for all pages
const MODAL_WARNING_TIME = 5 * 60 * 1000; // Show modal 5 minutes before timeout (at 5-minute mark)
const COUNTDOWN_INTERVAL = 1000; // Update countdown every second
const SESSION_STORAGE_KEY = "last_activity_time";
const SESSION_ACTIVITY_SESSION_ID_KEY = "last_activity_session_id";
const AUTO_REFRESH_INTERVAL = 10 * 60 * 1000; // Auto-refresh every 10 minutes for login/home pages
const PROACTIVE_REFRESH_INTERVAL = 10 * 60 * 1000; // 10 minutes - trigger API if user active this long
const MOUSE_ACTIVITY_THROTTLE = 30 * 1000; // 30 seconds - throttle mouse movement updates
const LOGOUT_PATH = zoneUrl("booking", "/logout");
const DEV_MAIN_APP_ORIGIN = "http://localhost:3000";

interface TokenRefreshProviderProps {
  children: React.ReactNode;
  sessionModalContent: SessionModal;
}

type TokenRefreshContextType = {
  isSessionReady: boolean;
  refreshToken: () => Promise<boolean>;
  getNewToken: () => Promise<boolean>;
};

export const TokenRefreshContext = createContext<TokenRefreshContextType | null>(null);

export const useTokenRefresh = () => {
  const context = useContext(TokenRefreshContext);
  if (!context) {
    throw new Error("useTokenRefresh must be used within a TokenRefreshProvider");
  }
  return context;
};

export default function TokenRefreshProvider({
  children,
  sessionModalContent,
}: TokenRefreshProviderProps) {
  const pathname = useZonePathname();

  // Determine if we're on login/home pages (no activity tracking, auto-refresh only)
  const isNoTrackingPage =
    pathname === "/" ||
    pathname.startsWith("/login") ||
    pathname === "/reset-password" ||
    pathname.startsWith("/home");

  // State
  const [showModal, setShowModal] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(60);
  const [isSessionReady, setIsSessionReady] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Refs
  const idleTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownTimerRef = useRef<NodeJS.Timeout | null>(null);
  const autoRefreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const lastTokenRefreshRef = useRef<number>(Date.now());
  const lastMouseActivityUpdateRef = useRef<number>(0);
  const showModalRef = useRef<boolean>(false);
  const expiredSessionIdRef = useRef<string | null>(null);

  // Keep showModalRef in sync with showModal state
  // This allows event handlers to read the latest value without stale closures
  useEffect(() => {
    showModalRef.current = showModal;
  }, [showModal]);

  /**
   * Get the last activity time from sessionStorage
   */
  const getLastActivityTime = useCallback((): number => {
    if (typeof window === "undefined") return Date.now();
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? parseInt(stored, 10) : Date.now();
  }, []);

  /**
   * Update the last activity time in sessionStorage
   */
  const updateLastActivityTime = useCallback(() => {
    const now = Date.now();
    lastActivityRef.current = now;
    if (typeof window !== "undefined") {
      sessionStorage.setItem(SESSION_STORAGE_KEY, now.toString());

      const currentSessionId = getTabSessionId();
      if (currentSessionId) {
        sessionStorage.setItem(SESSION_ACTIVITY_SESSION_ID_KEY, currentSessionId);
      } else {
        sessionStorage.removeItem(SESSION_ACTIVITY_SESSION_ID_KEY);
      }
    }
  }, []);

  /**
   * Refresh the token by calling the backend
   */
  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      const tabSessionId = getTabSessionId();
      if (!tabSessionId) {
        return false;
      }

      const success = await refreshTokenAction(tabSessionId);

      if (success) {
        // Update activity time after successful refresh
        updateLastActivityTime();
      }

      return success;
    } catch (error) {
      console.error("[TokenRefresh] Error refreshing token:", error);
      return false;
    }
  }, [updateLastActivityTime]);

  /**
   * Get a new token (for session restart)
   * Wrapper around server action that also manages refresh state and activity time
   */
  const getNewToken = useCallback(async (): Promise<boolean> => {
    const tabSessionId = getTabSessionId();
    if (!tabSessionId) {
      console.error("[TokenRefresh] No tab session ID found");
      return false;
    }

    const success = await getNewTokenAction(tabSessionId);

    if (success) {
      // Update activity time after getting new token
      updateLastActivityTime();
    }

    return success;
  }, [updateLastActivityTime]);

  const buildLogoutRedirectUrl = useCallback((): string => {
    const isLocalhost = typeof window !== "undefined" && window.location.hostname === "localhost";

    return isLocalhost ? `${DEV_MAIN_APP_ORIGIN}${LOGOUT_PATH}` : LOGOUT_PATH;
  }, []);

  /**
   * Clear all timers
   */
  const clearAllTimers = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
    if (countdownTimerRef.current) {
      clearInterval(countdownTimerRef.current);
      countdownTimerRef.current = null;
    }
    if (autoRefreshTimerRef.current) {
      clearInterval(autoRefreshTimerRef.current);
      autoRefreshTimerRef.current = null;
    }
  }, []);

  /**
   * Start auto-refresh timer for login/home pages
   * Refreshes token every 10 minutes in background without showing modal
   */
  const startAutoRefresh = useCallback(() => {
    clearAllTimers();

    // Don't refresh immediately on page load - only set up interval
    // This prevents trying to refresh anonymous/unauthenticated tokens
    // Token will be refreshed every 10 minutes starting from now
    autoRefreshTimerRef.current = setInterval(() => {
      refreshToken();
    }, AUTO_REFRESH_INTERVAL);
  }, [clearAllTimers, refreshToken]);

  /**
   * Start the countdown timer (1 minute before expiry)
   */
  const startCountdown = useCallback(() => {
    setRemainingSeconds(MODAL_WARNING_TIME / 1000);
    setShowModal(true);

    countdownTimerRef.current = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          // Time's up - need to get new token
          if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
          }
          return 0;
        }
        return prev - 1;
      });
    }, COUNTDOWN_INTERVAL);
  }, []);

  /**
   * Start the idle timer for all pages
   */
  const startIdleTimer = useCallback(() => {
    clearAllTimers();

    const timeUntilWarning = SESSION_TIMEOUT - MODAL_WARNING_TIME;

    idleTimerRef.current = setTimeout(() => {
      // Show warning modal 5 minutes before timeout
      startCountdown();
    }, timeUntilWarning);
  }, [clearAllTimers, startCountdown]);

  /**
   * Reset the activity timer
   */
  const resetActivityTimer = useCallback(() => {
    updateLastActivityTime();
    setShowModal(false);
    clearAllTimers();
    startIdleTimer();
  }, [updateLastActivityTime, clearAllTimers, startIdleTimer]);

  /**
   * Handle mouse movement activity
   * Updates session storage and proactively refreshes token if needed
   * Does NOT dismiss modal once it's showing - user must click "Continue my session"
   */
  const handleMouseActivity = useCallback(() => {
    if (isNoTrackingPage) return;

    const now = Date.now();

    // Throttle updates to avoid excessive sessionStorage writes
    if (now - lastMouseActivityUpdateRef.current < MOUSE_ACTIVITY_THROTTLE) {
      return;
    }

    lastMouseActivityUpdateRef.current = now;

    // Update session storage (frontend tracking only, does NOT update _last_api_activity cookie)
    updateLastActivityTime();

    // ONLY reset idle timer if modal is NOT showing
    // If modal is already open, user MUST click "Continue my session" - mouse movement doesn't count
    if (!showModalRef.current) {
      clearAllTimers();
      startIdleTimer();

      // Check if we need to proactively refresh the token
      // Only refresh if:
      // 1. Modal is not showing (session is still active)
      // 2. 10+ minutes have passed since last refresh
      const timeSinceLastRefresh = now - lastTokenRefreshRef.current;
      if (timeSinceLastRefresh >= PROACTIVE_REFRESH_INTERVAL) {
        // Trigger token refresh to keep backend session alive
        refreshToken().then((success) => {
          if (success) {
            lastTokenRefreshRef.current = now;
          }
        });
      }
    }
    // If modal IS showing, do nothing - let the timer continue counting down
    // User must click "Continue" before the 5 minutes expire or session will timeout
  }, [isNoTrackingPage, updateLastActivityTime, clearAllTimers, startIdleTimer, refreshToken]);

  /**
   * Handle extending the session
   */
  const handleExtendSession = useCallback(async () => {
    // Only attempt refresh if session hasn't fully expired
    // (This is the "Continue my session" button click handler)
    const success = await refreshToken();
    if (success) {
      lastTokenRefreshRef.current = Date.now(); // Track when we refreshed
      resetActivityTimer();
    } else {
      setRemainingSeconds(0);
    }
  }, [refreshToken, resetActivityTimer]);

  /**
   * Handle starting a new session
   */
  const handleStartNewSession = useCallback(async () => {
    setIsLoggingOut(true);
    try {
      let currentSessionId: string | null = null;

      if (typeof window !== "undefined") {
        currentSessionId = getTabSessionId() ?? expiredSessionIdRef.current;

        // Helper function to clear all redux-persist related keys from a storage
        const clearAllReduxKeys = (storage: Storage, sessionId: string | null) => {
          const keysToRemove: string[] = [];

          // Collect all keys that might contain Redux data
          for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (
              key &&
              (key.includes("persist") || key.includes("redux") || key.includes("Redux"))
            ) {
              keysToRemove.push(key);
            }
          }

          // Remove all collected keys
          keysToRemove.forEach((key) => {
            try {
              storage.removeItem(key);
            } catch (err) {
              console.warn(`Failed to remove key ${key}:`, err);
            }
          });

          // Also explicitly try session-specific keys if we have a session ID
          if (sessionId) {
            try {
              storage.removeItem(`persist:redux:${sessionId}`);
              storage.removeItem(`redux:${sessionId}`);
            } catch (err) {
              console.warn("Failed to remove session-specific keys:", err);
            }
          }

          // Also remove the generic persist:redux key (without session ID)
          // This prevents fallback reading of stale data when session ID is null
          try {
            storage.removeItem("persist:redux");
          } catch (err) {
            console.warn("Failed to remove generic key:", err);
          }
        };

        // Clear from both localStorage and sessionStorage
        try {
          clearAllReduxKeys(window.localStorage, currentSessionId);
          clearAllReduxKeys(window.sessionStorage, currentSessionId);
        } catch (error) {
          console.warn("[TokenRefresh] Failed to clear persisted Redux state", error);
        }

        // Clear session activity tracking keys
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
        sessionStorage.removeItem(SESSION_ACTIVITY_SESSION_ID_KEY);
      }

      clearTabSessionId();

      expiredSessionIdRef.current = null;

      setShowModal(false);
    } catch (error) {
      console.error("Error during session restart:", error);
      setIsLoggingOut(false);
    }

    window.location.href = buildLogoutRedirectUrl();
  }, [buildLogoutRedirectUrl]);

  /**
   * Poll for tab activity cookie changes
   * The _last_api_activity cookie is set by apiClient only for authenticated requests (withAuth: true)
   * This ensures session timeout only tracks authenticated API activity
   */
  useEffect(() => {
    if (typeof window === "undefined" || isNoTrackingPage) {
      return;
    }

    let lastSeenTimestamp = 0;

    const interval = window.setInterval(() => {
      const tabSessionId = getTabSessionId();
      if (!tabSessionId) {
        return;
      }

      const cookieEntry = document.cookie
        .split(";")
        .map((entry) => entry.trim())
        .find((entry) => entry.startsWith(`${TAB_ACTIVITY_COOKIE_NAME}=`));

      if (!cookieEntry) {
        return;
      }

      const rawValue = cookieEntry.split("=").slice(1).join("=");
      if (!rawValue) {
        return;
      }

      const decodedValue = decodeURIComponent(rawValue);
      const [cookieTabSessionId, timestampPart] = decodedValue.split(TAB_ACTIVITY_COOKIE_DELIMITER);

      if (cookieTabSessionId !== tabSessionId) {
        return;
      }

      const timestamp = Number.parseInt(timestampPart ?? "", 10);
      if (Number.isNaN(timestamp) || timestamp <= lastSeenTimestamp) {
        return;
      }

      lastSeenTimestamp = timestamp;
      // Update last_activity_time when we detect authenticated API activity
      // The cookie is only set by apiClient when withAuth: true
      updateLastActivityTime();
      // Also reset the idle timer
      setShowModal(false);
      clearAllTimers();
      startIdleTimer();
    }, TAB_ACTIVITY_COOKIE_POLL_INTERVAL_MS);

    return () => window.clearInterval(interval);
  }, [clearAllTimers, startIdleTimer, updateLastActivityTime, isNoTrackingPage]);

  /**
   * Initialize timers based on current page
   * - Login/Home pages: Auto-refresh every 10 minutes, no modal
   * - Flight app: Activity tracking with modal warning
   * This handles page refreshes by calculating elapsed time from sessionStorage
   */
  useEffect(() => {
    // Login/Home pages: Start auto-refresh timer
    if (isNoTrackingPage) {
      // Ensure we start with fresh activity time when on untracked pages
      // This prevents stale timestamps from triggering immediate timeout when navigating to tracked pages
      updateLastActivityTime();
      startAutoRefresh();

      return () => {
        // Also update when leaving the untracked page (e.g. navigating to Flight)
        // This ensures the new page receives a "just active" timestamp
        updateLastActivityTime();
        clearAllTimers();
      };
    }

    // Flight app: Activity tracking with modal
    // Get last activity time from sessionStorage
    let lastActivity = getLastActivityTime();
    const storedActivitySessionId =
      typeof window !== "undefined"
        ? sessionStorage.getItem(SESSION_ACTIVITY_SESSION_ID_KEY)
        : null;
    const currentTabSessionId = getTabSessionId();

    // If we just received a brand-new tab session (e.g., coming from Main to Flight),
    // discard any stale timestamps from older sessions so we don't instantly expire.
    if (currentTabSessionId && storedActivitySessionId !== currentTabSessionId) {
      lastActivity = Date.now();
      if (typeof window !== "undefined") {
        sessionStorage.setItem(SESSION_STORAGE_KEY, lastActivity.toString());
        sessionStorage.setItem(SESSION_ACTIVITY_SESSION_ID_KEY, currentTabSessionId);
      }
    } else if (typeof window !== "undefined" && !sessionStorage.getItem(SESSION_STORAGE_KEY)) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, lastActivity.toString());
      if (currentTabSessionId) {
        sessionStorage.setItem(SESSION_ACTIVITY_SESSION_ID_KEY, currentTabSessionId);
      }
    }

    lastActivityRef.current = lastActivity;

    const now = Date.now();
    const timeSinceLastActivity = now - lastActivity;

    // All pages: idle timer with modal warning
    const timeUntilWarning = SESSION_TIMEOUT - MODAL_WARNING_TIME;

    if (timeSinceLastActivity >= SESSION_TIMEOUT) {
      // Session already expired - show modal immediately
      setRemainingSeconds(0);
      setShowModal(true);
    } else if (timeSinceLastActivity >= timeUntilWarning) {
      // Already in warning period - show modal with remaining time
      const remainingTime = SESSION_TIMEOUT - timeSinceLastActivity;
      const remainingSec = Math.max(1, Math.ceil(remainingTime / 1000));

      setRemainingSeconds(remainingSec);
      setShowModal(true);

      countdownTimerRef.current = setInterval(() => {
        setRemainingSeconds((prev) => {
          if (prev <= 1) {
            if (countdownTimerRef.current) {
              clearInterval(countdownTimerRef.current);
              countdownTimerRef.current = null;
            }
            return 0;
          }
          return prev - 1;
        });
      }, COUNTDOWN_INTERVAL);
    } else {
      // Normal case - schedule warning for remaining time
      const timeUntilWarningFromNow = timeUntilWarning - timeSinceLastActivity;

      idleTimerRef.current = setTimeout(() => {
        startCountdown();
      }, timeUntilWarningFromNow);
    }

    return () => {
      clearAllTimers();
    };
  }, [
    getLastActivityTime,
    startIdleTimer,
    clearAllTimers,
    refreshToken,
    startCountdown,
    isNoTrackingPage,
    startAutoRefresh,
    updateLastActivityTime,
  ]);

  /**
   * Handle page visibility changes
   * Only checks if session expired while tab was hidden
   * Does NOT reset timers - they must stay in sync with backend token expiry
   * ONLY ACTIVE FOR FLIGHT APP - not for login/home pages
   */
  useEffect(() => {
    if (isNoTrackingPage) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        const lastActivity = getLastActivityTime();
        const timeSinceLastActivity = Date.now() - lastActivity;

        if (timeSinceLastActivity >= SESSION_TIMEOUT) {
          // Session has expired while tab was hidden
          // Show modal immediately so user knows what happened
          setRemainingSeconds(0);
          setShowModal(true);
        }
        // If session is still valid, do nothing
        // Let the existing timers continue based on actual API activity
        // This keeps frontend in sync with backend token expiry
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [getLastActivityTime, isNoTrackingPage]);

  /**
   * Track mouse movement to detect user activity
   * Only active for booking pages (not login/home pages)
   */
  useEffect(() => {
    if (isNoTrackingPage || typeof window === "undefined") {
      return;
    }

    window.addEventListener("mousemove", handleMouseActivity, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handleMouseActivity);
    };
  }, [handleMouseActivity, isNoTrackingPage]);

  // Wait for session initialization before checking token
  useEffect(() => {
    const initSession = async () => {
      // Check if session ID exists immediately
      const currentTabSessionId = getTabSessionId();

      if (currentTabSessionId) {
        setIsSessionReady(true);
        return;
      }

      // Wait for session-initialized event ONLY if we don't have an ID yet
      const sessionReady = await new Promise<boolean>((resolve) => {
        const timeout = setTimeout(() => {
          console.warn("[TokenRefresh] Session initialization timeout");
          resolve(false);
        }, 1000);

        const handleSessionInit = (event: Event) => {
          clearTimeout(timeout);
          const customEvent = event as CustomEvent;
          resolve(!customEvent.detail?.error);
        };

        window.addEventListener("session-initialized", handleSessionInit, { once: true });
      });

      if (!sessionReady) {
        // Session not ready - operating in stateless mode (e.g. Home Page)
        setIsSessionReady(true); // Still set ready to not block UI
        return;
      }

      setIsSessionReady(true);
    };

    initSession();
  }, []);

  useEffect(() => {
    if (!isNoTrackingPage && showModal && remainingSeconds === 0) {
      const expiredSessionId = getTabSessionId();

      clearTabSessionId();

      if (expiredSessionId) {
        expiredSessionIdRef.current = expiredSessionId;
      }
    }
  }, [isNoTrackingPage, showModal, remainingSeconds]);

  return (
    <TokenRefreshContext.Provider value={{ isSessionReady, refreshToken, getNewToken }}>
      {children}
      {/* Only show modal for flight app (activity tracking pages) */}
      {!isNoTrackingPage && (
        <SessionTimeoutModal
          open={showModal}
          remainingSeconds={remainingSeconds}
          onExtend={handleExtendSession}
          onLogout={handleStartNewSession}
          isLoggingOut={isLoggingOut}
          expiredTitle={sessionModalContent?.expiredTitle || "Your session has expired"}
          activeTitle={sessionModalContent?.activeTitle || "Your session is about to expire"}
          expiredDescription={
            sessionModalContent?.expiredDescription || "Please start a new session to continue."
          }
          activeDescription={
            sessionModalContent?.activeDescription || "Do you want to extend your session?"
          }
          extendButtonText={sessionModalContent?.extendButtonLabel || "Continue my session"}
          logoutButtonText={sessionModalContent?.startButtonLabel || "Start again"}
        />
      )}
    </TokenRefreshContext.Provider>
  );
}
